import type {
  WorkflowApprovalStepDefinition,
  WorkflowFieldDefinition,
  WorkflowRequest,
  WorkflowTask,
  WorkflowTemplate,
  WorkflowTemplateIconKey,
} from '../model';
import type {
  WorkflowApprovalLabel,
  WorkflowApprovalRoleOption,
  WorkflowApprovalTemplate,
} from '../approval-settings/public';
import {
  fixtureEmployeeDirectory,
  type EmployeeDirectorySnapshot,
} from '../../people/public';

export const defaultFields = (): WorkflowFieldDefinition[] => [{
  key: 'request_detail', order: 1, type: 'LONG_TEXT', label: '요청 내용', required: true,
  sensitivity: 'CONFIDENTIAL', searchable: false, exportable: true,
}];

export const fixtureExpenseFields = (): WorkflowFieldDefinition[] => [
  { key: 'expense_name', order: 1, type: 'SHORT_TEXT', label: '지출 항목', helpText: '무엇을 위한 비용인지 입력하세요.', required: false, sensitivity: 'INTERNAL', searchable: true, exportable: true },
  { key: 'amount', order: 2, type: 'CURRENCY', label: '금액', helpText: '부가세를 포함한 예상 금액', required: false, sensitivity: 'CONFIDENTIAL', searchable: false, exportable: true, config: { currency: 'KRW' }, validation: { min: 0 } },
  { key: 'expense_type', order: 3, type: 'SINGLE_SELECT', label: '비용 유형', required: false, sensitivity: 'INTERNAL', searchable: true, exportable: true, config: { options: [{ value: 'travel', label: '출장·교통' }, { value: 'software', label: '소프트웨어' }, { value: 'other', label: '기타' }] } },
  { ...defaultFields()[0], order: 4 },
];

export const fixtureDocumentFields = (): WorkflowFieldDefinition[] => [
  { key: 'attachments', order: 1, type: 'FILE', label: '검토 파일', helpText: '검토할 문서를 첨부하세요.', required: true, sensitivity: 'CONFIDENTIAL', searchable: false, exportable: true, config: { maxFiles: 8 } },
  { key: 'related_request', order: 2, type: 'WORKFLOW_REFERENCE', label: '관련 워크플로우 문서', helpText: '연결할 기존 요청을 검색하세요.', required: false, sensitivity: 'INTERNAL', searchable: false, exportable: true },
  { ...defaultFields()[0], order: 3 },
];

export const defaultApprovalSteps = (): WorkflowApprovalStepDefinition[] => [
  { order: 1, stageOrder: 1, label: '1차 조직장 승인', approvalLabel: { name: '승인', tone: 'GREEN' }, resolverType: 'REQUESTER_ORG_LEADER', resolverConfig: { level: 1 }, missingPolicy: 'FAIL', selfPolicy: 'SKIP', duplicatePolicy: 'SKIP', requesterEditable: true },
  { order: 2, stageOrder: 2, label: '2차 조직장 승인', approvalLabel: { name: '승인', tone: 'GREEN' }, resolverType: 'REQUESTER_ORG_LEADER', resolverConfig: { level: 2 }, missingPolicy: 'FALLBACK_ROOT_ORG_LEADER', selfPolicy: 'SKIP', duplicatePolicy: 'SKIP', requesterEditable: true },
];

export const fixtureApprovalSteps = () => defaultApprovalSteps().map((step, index) => ({ ...step, id: `workflow-expense-step-${index + 1}`, requesterEditable: index > 0 }));
export const fixtureLockedApprovalSteps = () => defaultApprovalSteps().map((step, index) => ({ ...step, id: `workflow-security-step-${index + 1}`, requesterEditable: false }));

export function fixtureCatalogTemplate({ id, code, displayName, category, iconKey, description, fields = defaultFields() }: { id: string; code: string; displayName: string; category: string; iconKey: WorkflowTemplateIconKey; description: string; fields?: WorkflowFieldDefinition[] }): WorkflowTemplate {
  return {
    id,
    code,
    displayName,
    category,
    iconKey,
    status: 'ACTIVE',
    editRevision: 1,
    updatedAt: '2026-08-25T09:00:00Z',
    version: {
      id: `${id}-v1`,
      version: 1,
      status: 'PUBLISHED',
      description,
      fields,
      approvalSteps: defaultApprovalSteps().map((step, index) => ({ ...step, id: `${id}-step-${index + 1}` })),
      accessRules: [{ capability: 'REQUEST', principalType: 'ALL_EMPLOYEES', includeDescendants: true }],
    },
  };
}

export const fixtureTemplates: WorkflowTemplate[] = [
  {
    id: 'workflow-expense', code: 'EXPENSE_REQUEST', displayName: '비용 지급 요청', category: '재무', iconKey: 'WALLET', status: 'ACTIVE', editRevision: 1, updatedAt: '2026-08-25T09:00:00Z',
    version: { id: 'workflow-expense-v1', version: 1, status: 'PUBLISHED', description: '지출 정보와 증빙 내용을 작성해 결재를 요청합니다.', fields: fixtureExpenseFields(), approvalSteps: fixtureApprovalSteps(), accessRules: [{ capability: 'REQUEST', principalType: 'ALL_EMPLOYEES', includeDescendants: true }] },
  },
  {
    id: 'workflow-security', code: 'SECURITY_EXCEPTION', displayName: '보안 예외 신청', category: '보안', iconKey: 'SHIELD', status: 'ACTIVE', editRevision: 1, updatedAt: '2026-08-25T09:00:00Z',
    version: { id: 'workflow-security-v1', version: 1, status: 'PUBLISHED', description: '관리자가 지정한 결재선을 변경하지 않고 보안 예외를 신청합니다.', fields: defaultFields(), approvalSteps: fixtureLockedApprovalSteps(), accessRules: [{ capability: 'REQUEST', principalType: 'ALL_EMPLOYEES', includeDescendants: true }] },
  },
  fixtureCatalogTemplate({ id: 'workflow-software-purchase', code: 'SOFTWARE_PURCHASE', displayName: '소프트웨어 구매 신청', category: '구매', iconKey: 'CART', description: '업무에 필요한 소프트웨어와 사용 목적을 작성합니다.' }),
  fixtureCatalogTemplate({ id: 'workflow-equipment-purchase', code: 'EQUIPMENT_PURCHASE', displayName: '장비 구매 요청', category: '구매', iconKey: 'EQUIPMENT', description: '업무 장비의 사양과 필요 수량을 작성해 구매를 요청합니다.' }),
  fixtureCatalogTemplate({ id: 'workflow-contract-review', code: 'CONTRACT_REVIEW', displayName: '계약 검토 요청', category: '법무', iconKey: 'CONTRACT', description: '체결 전 계약서와 검토가 필요한 쟁점을 전달합니다.', fields: fixtureDocumentFields() }),
  fixtureCatalogTemplate({ id: 'workflow-external-activity', code: 'EXTERNAL_ACTIVITY', displayName: '외부 활동 사전 신청', category: '공통', iconKey: 'PEOPLE', description: '강연과 기고 등 회사 밖 활동을 사전에 공유합니다.' }),
  fixtureCatalogTemplate({ id: 'workflow-system-access', code: 'SYSTEM_ACCESS_REQUEST', displayName: '업무 시스템 권한 신청', category: 'IT', iconKey: 'ACCESS', description: '업무에 필요한 시스템과 접근 권한을 신청합니다.' }),
  fixtureCatalogTemplate({ id: 'workflow-refresh-leave', code: 'REFRESH_LEAVE_REQUEST', displayName: '리프레시 휴가 신청', category: '휴가', iconKey: 'CALENDAR', description: '리프레시 휴가 일정과 업무 인계 내용을 작성합니다.' }),
];

export const fixtureApprovalLabels: WorkflowApprovalLabel[] = [
  { id: '11111111111111111111111111111111', name: '합의', description: '관련 부서와 의견을 맞추는 단계', tone: 'BLUE', status: 'ACTIVE', version: 1, createdAt: '2026-08-25T09:00:00Z', updatedAt: '2026-08-25T09:00:00Z' },
  { id: '22222222222222222222222222222222', name: '결재', description: '지출과 계약의 최종 의사결정', tone: 'PURPLE', status: 'ACTIVE', version: 1, createdAt: '2026-08-25T09:00:00Z', updatedAt: '2026-08-25T09:00:00Z' },
];

export const fixtureApprovalTemplates: WorkflowApprovalTemplate[] = [{
  id: '33333333333333333333333333333333',
  name: '지출 기본 결재선',
  description: '조직장 합의 후 재무 결재를 받습니다.',
  status: 'ACTIVE',
  version: 1,
  approvalSteps: [
    { ...defaultApprovalSteps()[0], label: '재무 합의', resolverType: 'FIXED_PERSON', resolverConfig: { personId: 'person-06' }, requesterEditable: true, approvalLabelId: fixtureApprovalLabels[0].id, approvalLabel: fixtureApprovalLabels[0] },
    { ...defaultApprovalSteps()[1], label: '지출 결재', resolverType: 'FIXED_PERSON', resolverConfig: { personId: 'person-01' }, requesterEditable: true, approvalLabelId: fixtureApprovalLabels[1].id, approvalLabel: fixtureApprovalLabels[1] },
  ],
  createdAt: '2026-08-25T09:00:00Z',
  updatedAt: '2026-08-25T09:00:00Z',
}];

export const fixtureWorkflowEmployeeDirectory: EmployeeDirectorySnapshot = {
  organizations: fixtureEmployeeDirectory.organizations.map((organization) => organization.id === 'product'
    ? { ...organization, managerId: 'product-leader' }
    : organization),
  members: [...fixtureEmployeeDirectory.members.map((member) => member.id === '01'
    ? { ...member, profilePhotoUrl: '/images/avatars/jung-seongjin.svg' }
    : member), {
    id: 'product-leader',
    personId: 'person-product-leader',
    name: '윤서연',
    title: 'VP of Product',
    organizationId: 'product',
    profilePhotoUrl: '/images/avatars/yun-seoyeon.svg',
  }],
};

export const fixtureWorkflowApprovalRoles: WorkflowApprovalRoleOption[] = [
  { roleCode: 'FINANCE_APPROVER', descriptor: '재무 결재 담당자' },
  { roleCode: 'LEGAL_REVIEWER', descriptor: '법무 검토 담당자' },
  { roleCode: 'HEYDAY_ADMIN', descriptor: 'HRIS 전체 관리자' },
];

export const fixtureRequests: WorkflowRequest[] = [{
  id: 'request-1', templateId: 'workflow-expense', templateVersionId: 'workflow-expense-v1', templateName: '비용 지급 요청', templateIconKey: 'WALLET', templateVersion: 1,
  title: '8월 팀 워크숍 비용', status: 'IN_PROGRESS', requesterPersonId: 'person-me', requesterName: '김소카', createdAt: '2026-08-25T09:00:00Z', updatedAt: '2026-08-25T09:10:00Z', submittedAt: '2026-08-25T09:10:00Z',
  values: { expense_name: '8월 팀 워크숍', amount: 1200000, expense_type: 'travel', request_detail: '팀 워크숍 비용 지급을 요청합니다.' }, fields: fixtureExpenseFields(), approvalSteps: [
    { order: 1, stageOrder: 1, label: '1차 조직장 승인', approvalLabel: { name: '합의', tone: 'BLUE' }, assigneeType: 'PERSON', assigneeId: 'person-02', status: 'COMPLETED' },
    { order: 2, stageOrder: 2, label: '2차 조직장 승인', approvalLabel: { name: '결재', tone: 'PURPLE' }, assigneeType: 'PERSON', assigneeId: 'person-01', status: 'IN_PROGRESS' },
  ],
}];

export const fixtureReferenceOptions: WorkflowRequest[] = [
  ...fixtureRequests,
  {
    ...fixtureRequests[0],
    id: 'request-reference-2',
    templateId: 'workflow-contract-review',
    templateVersionId: 'workflow-contract-review-v1',
    templateName: '계약 검토 요청',
    title: '신규 공급사 계약 검토',
    status: 'COMPLETED',
    createdAt: '2026-08-20T03:00:00Z',
    updatedAt: '2026-08-21T06:30:00Z',
    submittedAt: '2026-08-20T03:10:00Z',
  },
];

export const fixtureTasks: WorkflowTask[] = [];
